import { useMemo } from "react";
import { ActiveProgramStats } from "common/build/api-parameters/users";
import { useAuth } from "../contexts/auth.context";
import { useFetch } from "../core/components/crud/contexts/fetch.context";
import { justCompletedSessionIds } from "../utils/sessionNav";

/** School create flow: pick a classroom, then launch the classroom programme wizard. */
export const SCHOOL_CREATE_PROGRAMME_PATH = "/classrooms/create-programme";

export function useNextSession() {
    const { user } = useAuth();
    const { item, fetching, error } = useFetch<ActiveProgramStats>();

    const sessions = useMemo(() => {
        // Keep the last successful list while stats refetch, so Prepare/Deliver do not
        // briefly point at the hub (or a just-finished session) after leaving Deliver.
        if (error || !item?.sessions?.length) {
            return [];
        }

        let list = [...item.sessions];
        if (user?.isSchool) {
            list = list.filter((session) => session.Programme?.classroomId);
        }

        return list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [item?.sessions, user?.isSchool, error]);

    const nextSession = useMemo(() => {
        if (!sessions.length) {
            return null;
        }

        const skipped = justCompletedSessionIds();
        // Skip-list is only for stale InProgress rows after a real complete.
        // Never hide NotStarted — a brand-new programme must stay actionable.
        const actionable = sessions.filter((session) => session.state === "NotStarted" || !skipped.has(session.id));
        return actionable.find((session) => session.state === "NotStarted" || session.state === "InProgress") ?? null;
    }, [sessions]);

    const nextSessionId = nextSession?.id ?? null;
    const preparePath = nextSessionId ? `/prepare-session/${nextSessionId}` : "/prepare";
    const deliverPath = nextSessionId ? `/deliver-session/${nextSessionId}` : "/deliver";

    return { nextSession, nextSessionId, preparePath, deliverPath, sessions, fetching, error };
}
